import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';

import {
  BoxShadow,
  ColorWhite,
  Gutter,
  TransitionGlobal,
} from '../../../shared/GlobalStyles';

const getModalStyle = ({
  backgroundColor = ColorWhite,
  borderRadius = '12px',
  margin = '20px',
  marginTop = '40px',
  maxWidth,
  minHeight,
  minWidth = '380px',
  overflowY = 'auto',
  width,
}) => ({
  overlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    height: '100vh',
    overflowY,
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: '100',
  },
  content: {
    backgroundColor,
    borderRadius,
    boxShadow: `${BoxShadow}`,
    display: 'flex',
    flexDirection: 'column',
    margin,
    marginTop,
    ...(width && { width }),
    ...(maxWidth && { maxWidth }),
    ...(minWidth && { minWidth }),
    ...(minHeight && { minHeight }),
  },
});

export const ModalContentWrapper = styled('div')`
  background-color: ${(props) => props.theme.backgroundColorL2};
  display: flex;
  flex-grow: 1;
  ${(props) => props.customPadding && `padding: ${props.customPadding};`}

  form {
    ${(props) => (props.customPadding
    ? 'padding-bottom: 30px; padding-top: 30px; width: 100%;'
    : 'padding-bottom: 20px;')}
    ${(props) => props.customPadding
      && `
      .divider-line {
        border-top: 2px solid rgb(232,232,232);
        margin-bottom: 30px;
        margin-left: -80px;
        width: calc(100% + 160px);
      }
    `}

    label {
      color: ${(props) => props.theme.textColor};
    }

    input, textarea {
      background-color: ${(props) => props.theme.backgroundColorL3};
    }
  }

  .select__control, .select__menu, textarea {
    background-color: ${(props) => props.theme.backgroundColorL3};
    border-color: ${(props) => props.theme.noBorderColorForm};
    color: ${(props) => props.theme.textColor};
  }

  .select__single-value {
    color: ${(props) => props.theme.textColor};
  }

  ${(props) => props.showSpinnerContainer !== false
    && `
    .spinner-container {
      min-height: 300px;
    }
  `}

  ${(props) => (props.customPadding
    ? ''
    : `
    .form-group {
      padding: ${Gutter} calc(${Gutter} * 3);
    }
  `)}

  ${(props) => props.customPadding
    && `
    .form-control {
      min-height: 150px;
    }
  `}

  .react-pdf__Page__canvas {
    margin: 0 auto;
  }

  .modal-button {
    bottom: 0;
    ${(props) => (props.customPadding ? 'margin-left: -30px;' : 'margin-left: -40px;')}
    position: absolute;
  }

  @media only screen and (min-height: 581px) and (max-height: 768px) {
    .modal-button {
      margin-left: -20px;
    }
    ${(props) => (props.customPadding
    ? ''
    : `
      .title-container {
        margin-bottom: 0px;
      }
    `)}
  }

  @media only screen and (max-height: 580px) {
    .modal-button {
      margin-left: -20px;
    }
    ${(props) => (props.customPadding
    ? ''
    : `
      .title-container {
        display: none;
      }
    `)}
  }

  @media only screen and (max-width: 1024px) {
    .note-title-container {
      margin-bottom: 0px!important;
    }
  }
`;

const JournalModalWrapper = ({
  backgroundColor,
  borderRadius,
  children,
  currentRow,
  customModalHeight,
  customPadding,
  isOpen,
  margin,
  marginTop,
  maxWidth,
  minHeight,
  minWidth,
  onRequestClose,
  overlayBackgroundColor,
  overflowY,
  setCurrentRow,
  showSpinnerContainer,
  width,
}) => {
  const [modalDimensions, setModalDimensions] = useState({});
  const modalContentRef = useRef(null);

  const modalClose = useCallback(() => {
    onRequestClose();
    if (currentRow && setCurrentRow) {
      setCurrentRow(null);
    }
  }, [currentRow, onRequestClose, setCurrentRow]);

  useEffect(() => {
    if (customModalHeight && modalContentRef.current) {
      setTimeout(() => {
        setModalDimensions(modalContentRef.current.clientHeight);
      }, 800);
    }
  }, [isOpen, customModalHeight]);

  const modalStyle = getModalStyle({
    backgroundColor,
    borderRadius,
    margin,
    marginTop,
    maxWidth,
    minHeight: customModalHeight || minHeight,
    minWidth,
    overflowY,
    width,
  });

  if (overlayBackgroundColor !== 'rgba(216, 216, 216, 0.5)') {
    modalStyle.overlay.backgroundColor = overlayBackgroundColor;
  }

  return (
    <>
      <Global
        styles={css`
          .ReactModal__Content {
            opacity: 0;
            transform: translateY(50%);
            &:focus {
              outline: -webkit-focus-ring-color auto 0px !important;
            }
            ${width || maxWidth
          ? ''
          : `
              @media screen and (max-width: 580px) {
                min-width: 350px!important;
              }
            `}
          }
          .ReactModal__Overlay {
            background-color: ${overlayBackgroundColor};
            justify-content: center;
            opacity: 1;
            z-index: 1000 !important;
          }
          .ReactModal__Overlay--after-open {
            opacity: 1;
            transition: ${TransitionGlobal};
          }
          .ReactModal__Overlay--before-close {
            opacity: 0;
          }
          .ReactModal__Content--after-open {
            opacity: 1;
            transform: translateY(0);
            transition: ${TransitionGlobal};
          }
          .ReactModal__Content--before-close {
            opacity: 0;
            transform: translateY(50%);
          }
          @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
            .ReactModal__Content {
              ${width ? `width: ${width}!important;` : 'width: 75vw!important;'}
            }
          }
          @media only screen and (max-device-width: 767px) {
            .ReactModal__Content {
              ${width
            ? `min-width: ${width}!important;`
            : 'min-width: 95%!important;'}
            }
          }
          ${customModalHeight
              ? `
            @media screen and (max-height: ${modalDimensions}px) {
              .ReactModal__Content {
                position: absolute!important;
                top: 15px!important;
              }
            }
          `
              : `
            @media screen and (max-height: 780px) {
              .ReactModal__Content {
                position: absolute!important;
                top: 15px!important;
              }
            }
          `}
        `}
      />
      <ReactModal
        closeTimeoutMS={250}
        isOpen={!!isOpen}
        onRequestClose={modalClose}
        style={modalStyle}
        className='global-modal'
        overlayClassName='overlay'
      >
        <ModalContentWrapper
          ref={customModalHeight ? modalContentRef : null}
          customPadding={customPadding}
          showSpinnerContainer={showSpinnerContainer}
        >
          {children}
        </ModalContentWrapper>
      </ReactModal>
    </>
  );
};

if (process.env.STORYBOOK_RUNNING === undefined) {
  ReactModal.setAppElement('#app');
}

JournalModalWrapper.propTypes = {
  backgroundColor: PropTypes.string,
  borderRadius: PropTypes.string,
  children: PropTypes.node,
  currentRow: PropTypes.instanceOf(Object),
  customModalHeight: PropTypes.string,
  customPadding: PropTypes.string,
  isOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  margin: PropTypes.string,
  marginTop: PropTypes.string,
  maxWidth: PropTypes.string,
  minHeight: PropTypes.string,
  minWidth: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
  overlayBackgroundColor: PropTypes.string,
  overflowY: PropTypes.string,
  setCurrentRow: PropTypes.func,
  showSpinnerContainer: PropTypes.bool,
  width: PropTypes.string,
};

JournalModalWrapper.defaultProps = {
  backgroundColor: ColorWhite,
  borderRadius: '12px',
  children: null,
  currentRow: null,
  customModalHeight: null,
  customPadding: null,
  margin: '20px',
  marginTop: '40px',
  maxWidth: null,
  minHeight: null,
  minWidth: '380px',
  overlayBackgroundColor: 'rgba(216, 216, 216, 0.5)',
  overflowY: 'auto',
  setCurrentRow: null,
  showSpinnerContainer: true,
  width: null,
};

export default JournalModalWrapper;
